// section-tab-media.js — final patch: initialisation guard + centralized play + per-iframe state
(function () {
  'use strict';

  function toArray(nl) { return nl ? Array.prototype.slice.call(nl) : []; }
  function isMobile() { return window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : (window.innerWidth <= 749); }

  // per-iframe runtime state to avoid duplicate attempts / restarts
  var iframeState = new WeakMap();
  function getState(iframe) {
    var s = iframeState.get(iframe);
    if (!s) {
      s = { attempting: false, playing: false, lastAttempt: 0, lastPlayed: 0 };
      iframeState.set(iframe, s);
    }
    return s;
  }
  function setState(iframe, patch) {
    if (!iframe) return;
    var s = getState(iframe);
    for (var k in patch) if (patch.hasOwnProperty(k)) s[k] = patch[k];
    iframeState.set(iframe, s);
    return s;
  }

  // Prevent double-play during initial render / wiring
  var initialising = true;

  function addPreconnect() {
    var hosts = ['https://www.youtube.com', 'https://s.ytimg.com', 'https://www.google.com'];
    hosts.forEach(function(h) {
      try {
        if (!document.querySelector('link[rel="preconnect"][href="' + h + '"]')) {
          var l = document.createElement('link');
          l.rel = 'preconnect';
          l.href = h;
          l.crossOrigin = '';
          document.head.appendChild(l);
        }
      } catch (e) {}
    });
  }

  function postMessageCommand(iframe, func, args) {
    if (!iframe || !iframe.contentWindow) return false;
    var msg = { event: 'command', func: func, args: Array.isArray(args) ? args : (args === undefined ? [] : [args]) };
    try { iframe.contentWindow.postMessage(JSON.stringify(msg), '*'); return true; } catch (e) { return false; }
  }

  // Ensure iframe has src and warm attributes; returns Promise<boolean> when loaded or timeout
  function ensureIframeLoaded(iframe, opts) {
    return new Promise(function (resolve) {
      if (!iframe) return resolve(false);

      // consider loaded if it already has src and contentWindow
      if (iframe.src && iframe.contentWindow) return resolve(true);

      try {
        var src = iframe.dataset && iframe.dataset.src ? iframe.dataset.src : (iframe.getAttribute('src') || '');
        if (!src) return resolve(false);

        try { iframe.allow = iframe.allow || 'autoplay; encrypted-media; fullscreen'; } catch (e) {}
        try { iframe.loading = 'eager'; } catch (e) {}

        try {
          var u = new URL(src, window.location.href);
          u.searchParams.set('enablejsapi', '1');
          u.searchParams.set('playsinline', '1');
          iframe.dataset.origSrc = src;
          iframe.src = u.toString();
        } catch (e) {
          iframe.src = src;
        }

        var loaded = false;
        function onLoad() { if (loaded) return; loaded = true; cleanup(); resolve(true); }
        function onError() { if (loaded) return; loaded = true; cleanup(); resolve(false); }
        function cleanup() { try { iframe.removeEventListener('load', onLoad); iframe.removeEventListener('error', onError); } catch (e) {} }
        iframe.addEventListener('load', onLoad);
        iframe.addEventListener('error', onError);

        var to = (opts && opts.timeout) || 6000;
        setTimeout(function () { if (!loaded) { cleanup(); resolve(false); } }, to);
      } catch (e) { resolve(false); }
    });
  }

  // Try to play via API and listen for messages from the iframe to confirm playing
  function tryPlayViaAPI(iframe, attempts, interval, listenTimeout) {
    attempts = attempts || 10;
    interval = interval || 400;
    listenTimeout = listenTimeout || 6000;

    return new Promise(function (resolve) {
      if (!iframe) return resolve(false);

      var s = getState(iframe);
      var now = Date.now();

      // Prevent triggering if a recent attempt/playing already in progress
      if (s.attempting && (now - (s.lastAttempt || 0) < 3000)) return resolve(false);
      if (s.playing && (now - (s.lastPlayed || 0) < 2000)) return resolve(true);

      setState(iframe, { attempting: true, lastAttempt: now });

      var played = false;
      var attemptsCount = 0;
      var winListener = null;
      var finalTimeout = null;

      function onMessage(e) {
        if (!iframe || !iframe.contentWindow) return;
        if (e.source !== iframe.contentWindow) return;

        var data = e.data;
        try { if (typeof data === 'string') data = JSON.parse(data); } catch (e2) {}

        if (!data) return;

        // YouTube IFrame API: onReady & onStateChange patterns
        if (data.event === 'onReady') {
          postMessageCommand(iframe, 'mute');
          postMessageCommand(iframe, 'seekTo', [0, true]);
          postMessageCommand(iframe, 'playVideo');
        }

        var stateVal = data.info || data.data;
        if (typeof stateVal === 'number') {
          if (stateVal === 1) { // PLAYING
            played = true;
            setState(iframe, { playing: true, lastPlayed: Date.now(), attempting: false });
            cleanupAndResolve(true);
          } else if (stateVal === 0) { // ENDED
            setState(iframe, { playing: false });
          }
        }
      }

      function cleanupAndResolve(result) {
        if (winListener) window.removeEventListener('message', winListener);
        if (finalTimeout) clearTimeout(finalTimeout);
        setState(iframe, { attempting: false });
        return resolve(!!result);
      }

      winListener = onMessage;
      window.addEventListener('message', winListener);

      function attempt() {
        attemptsCount++;
        try { postMessageCommand(iframe, 'mute'); } catch (e) {}
        try { postMessageCommand(iframe, 'seekTo', [0, true]); } catch (e) {}
        postMessageCommand(iframe, 'playVideo');
        if (played) return cleanupAndResolve(true);
        if (attemptsCount >= attempts) return; // wait for final timeout
        setTimeout(attempt, interval);
      }

      attempt();

      finalTimeout = setTimeout(function () {
        if (played) return cleanupAndResolve(true);
        cleanupAndResolve(false);
      }, listenTimeout);
    });
  }

  // Last-resort fallback: rewrite src with autoplay=1&mute=1
  function fallbackAutoplay(iframe) {
    if (!iframe) return false;
    var s = getState(iframe);
    if (s.playing) return true;
    try {
      var base = iframe.dataset.origSrc || iframe.src || iframe.dataset.src;
      if (!base) return false;
      var u = new URL(base, window.location.href);
      u.searchParams.set('autoplay', '1');
      u.searchParams.set('mute', '1');
      u.searchParams.set('playsinline', '1');
      u.searchParams.set('enablejsapi', '1');
      u.searchParams.set('controls', '1');
      iframe.src = u.toString();
      setState(iframe, { lastAttempt: Date.now() });
      return true;
    } catch (e) { return false; }
  }

  function stopIframe(iframe) {
    if (!iframe) return;
    try { postMessageCommand(iframe, 'mute'); } catch (e) {}
    var ok = false;
    try { ok = postMessageCommand(iframe, 'stopVideo'); } catch (e) { ok = false; }
    if (ok) {
      setState(iframe, { playing: false, attempting: false });
      if (iframe.dataset.origSrc) try { iframe.src = iframe.dataset.origSrc; delete iframe.dataset.origSrc; } catch(e) {}
      return;
    }
    try {
      var base = iframe.dataset.origSrc || iframe.src;
      var u = new URL(base, window.location.href);
      u.searchParams.set('autoplay', '0');
      u.searchParams.set('mute', '1');
      iframe.src = u.toString();
      setState(iframe, { playing: false, attempting: false });
    } catch (e) {}
  }

  function findPlayerInElement(container) {
    if (!container) return null;
    var v = container.querySelector('video');
    if (v) return { type: 'html', el: v };
    var iframe = container.querySelector('iframe');
    if (iframe) return { type: 'iframe', el: iframe };
    var plyrIframe = container.querySelector('.plyr__video-embed iframe');
    if (plyrIframe) return { type: 'iframe', el: plyrIframe };
    return null;
  }

  function stopAllPlayers() {
    var root = document.querySelector('.tab-media');
    if (!root) return;
    var iframes = toArray(root.querySelectorAll('iframe'));
    iframes.forEach(function (f) { try { stopIframe(f); } catch (e) {} });
    var videos = toArray(root.querySelectorAll('video'));
    videos.forEach(function (v) { try { v.pause(); v.currentTime = 0; } catch (e) {} });
  }

  // Centralized play function — sets guards early to avoid race
  function playPlayerFromStart(player) {
    if (!player) return;
    // ensure only one player is active
    stopAllPlayers();

    if (player.type === 'html') {
      try {
        player.el.currentTime = 0;
        player.el.muted = true;
        player.el.play().catch(function () {});
        return;
      } catch (e) { return; }
    }

    var iframe = player.el;
    var s = getState(iframe);
    var now = Date.now();

    // guards: skip if already playing recently or attempting recently
    if (s.playing && (now - (s.lastPlayed || 0) < 2000)) return;
    if (s.attempting && (now - (s.lastAttempt || 0) < 3000)) return;

    // set attempting as early as possible
    setState(iframe, { attempting: true, lastAttempt: now });

    addPreconnect();

    ensureIframeLoaded(iframe, { timeout: 6000 }).then(function (loaded) {
      // small delay to let contentWindow become fully available on slow devices
      setTimeout(function () {
        tryPlayViaAPI(iframe, 12, 400, 8000).then(function (ok) {
          setState(iframe, { attempting: false });
          if (!ok) {
            fallbackAutoplay(iframe);
          } else {
            setState(iframe, { playing: true, lastPlayed: Date.now(), attempting: false });
          }
        }).catch(function () {
          setState(iframe, { attempting: false });
          fallbackAutoplay(iframe);
        });
      }, 350);
    }).catch(function () {
      setState(iframe, { attempting: false });
      fallbackAutoplay(iframe);
    });
  }

  // UI wiring & single-open behavior
  var programmaticToggle = new WeakMap();

  function closeOtherDetails(openDetail) {
    var details = toArray(document.querySelectorAll('.tab-media__accordion-item'));
    details.forEach(function (d) {
      if (d === openDetail) return;
      if (d.open) {
        programmaticToggle.set(d, 'closed');
        try { d.open = false; } catch (e) { try { d.removeAttribute('open'); } catch (e2) {} }
        try {
          var pl = findPlayerInElement(d);
          if (pl && pl.type === 'iframe') stopIframe(pl.el);
          else if (pl && pl.type === 'html') { pl.el.pause(); pl.el.currentTime = 0; }
        } catch (e) {}
      }
    });
  }

  function wireUI() {
    var tabs = toArray(document.querySelectorAll('.tab-media__tab-button'));
    var targets = toArray(document.querySelectorAll('.tab-media__target-item'));

    if (tabs.length) {
      tabs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-target');
          var el = document.querySelector('.tab-media__target-item[data-item="' + target + '"]');
          if (!el) return;
          tabs.forEach(function (b) { b.classList.remove('active'); });
          targets.forEach(function (t) { t.classList.remove('active'); });
          btn.classList.add('active');
          el.classList.add('active');
          var player = findPlayerInElement(el);
          playPlayerFromStart(player);
        });
      });
    }

    var details = toArray(document.querySelectorAll('.tab-media__accordion-item'));
    details.forEach(function (d) {
      d.addEventListener('toggle', function () {
        // ignore toggles during initialising window to avoid double-trigger
        if (initialising) {
          // mark as ignored if needed so we don't react to this synthetic state later
          programmaticToggle.set(d, 'ignored-in-init');
          return;
        }

        var marker = programmaticToggle.get(d);
        if (marker === 'open') { programmaticToggle.delete(d); }
        if (marker === 'closed') { programmaticToggle.delete(d); return; }
        if (marker === 'ignored-in-init') { programmaticToggle.delete(d); }

        if (d.open) {
          closeOtherDetails(d);
          var content = d.querySelector('.accordion__content') || d;
          var player = findPlayerInElement(content);
          playPlayerFromStart(player);
        } else {
          try {
            var p = findPlayerInElement(d);
            if (p && p.type === 'iframe') stopIframe(p.el);
            else if (p && p.type === 'html') { p.el.pause(); p.el.currentTime = 0; }
          } catch (e) {}
        }
      }, { passive: true });
    });
  }

  // Play first when visible once — mobile: first details already open on render, only play when visible
  function autoplayWhenVisibleOnce() {
    var section = document.querySelector('.tab-media');
    if (!section) {
      // if no section, end initialising after a short grace
      setTimeout(function () { initialising = false; }, 1200);
      return;
    }

    function playFirstWhenVisible() {
      if (isMobile()) {
        var firstDetails = document.querySelector('.tab-media__accordion-item');
        if (!firstDetails) {
          var el = document.querySelector('.tab-media__target-item') || document.querySelector('.tab-media__target-item.active');
          var pl = el && findPlayerInElement(el);
          if (pl) playPlayerFromStart(pl);
          // done — clear initialising after small grace so toggle handlers begin working
          setTimeout(function () { initialising = false; }, 1200);
          return;
        }

        // first details is expected to be open (you requested open on render)
        var content = firstDetails.querySelector('.accordion__content') || firstDetails;
        var player = findPlayerInElement(content);

        if (player) {
          if (player.type === 'iframe') {
            // guard against very-short double triggers
            var s = getState(player.el);
            if (s.playing && (Date.now() - (s.lastPlayed||0) < 2000)) {
              initialising = false;
              return;
            }
            if (s.attempting && (Date.now() - (s.lastAttempt||0) < 3000)) {
              initialising = false;
              return;
            }
            // route through centralized play function
            playPlayerFromStart(player);
            // after we've kicked off the play attempt, allow toggles after a short window
            setTimeout(function () { initialising = false; }, 1400);
          } else {
            // HTML video -> play via centralized function
            playPlayerFromStart(player);
            setTimeout(function () { initialising = false; }, 1200);
          }
          return;
        }

        // if player not yet present, observe for an insertion for a short while
        var mo = null;
        var timeout = setTimeout(function () {
          if (mo) mo.disconnect();
          var fallbackIframe = document.querySelector('.tab-media__accordion iframe[id^="player-mobile-"], .tab-media__accordion iframe, .tab-media__target-item iframe');
          if (fallbackIframe) playPlayerFromStart(findPlayerInElement(fallbackIframe.parentNode || fallbackIframe));
          // clear initialising after fallback attempt
          setTimeout(function () { initialising = false; }, 1200);
        }, 4000);

        try {
          if (window.MutationObserver) {
            mo = new MutationObserver(function (recs) {
              for (var i = 0; i < recs.length; i++) {
                var r = recs[i];
                if (r.addedNodes && r.addedNodes.length) {
                  for (var j = 0; j < r.addedNodes.length; j++) {
                    var n = r.addedNodes[j];
                    if (n && (n.tagName === 'IFRAME' || n.tagName === 'VIDEO' || (n.querySelector && (n.querySelector('iframe') || n.querySelector('video'))))) {
                      clearTimeout(timeout);
                      if (mo) mo.disconnect();
                      var plb = findPlayerInElement(content);
                      if (plb) playPlayerFromStart(plb);
                      // allow toggles shortly after we started the attempt
                      setTimeout(function () { initialising = false; }, 1200);
                      return;
                    }
                  }
                }
              }
            });
            mo.observe(section, { childList: true, subtree: true });
          }
        } catch (e) { clearTimeout(timeout); setTimeout(function () { initialising = false; }, 1200); }
        return;
      }

      // desktop: play first active/first target item as before and then clear initialising quickly
      var el = document.querySelector('.tab-media__target-item.active') || document.querySelector('.tab-media__target-item');
      var player = el && findPlayerInElement(el);
      if (player) playPlayerFromStart(player);
      setTimeout(function () { initialising = false; }, 1000);
    }

    if ('IntersectionObserver' in window) {
      var threshold = 0.35;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            playFirstWhenVisible();
            io.disconnect();
          }
        });
      }, { threshold: [threshold] });
      io.observe(section);
      return;
    }

    var fired = false;
    function onScrollCheck() {
      if (fired) return;
      var rect = section.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var visible = rect.top < vh * 0.85 && rect.bottom > vh * 0.15;
      if (visible) {
        fired = true;
        playFirstWhenVisible();
        window.removeEventListener('scroll', onScrollCheck);
        window.removeEventListener('resize', onScrollCheck);
      }
    }
    window.addEventListener('scroll', onScrollCheck, { passive: true });
    window.addEventListener('resize', onScrollCheck);
    setTimeout(onScrollCheck, 160);
  }

  function init() {
    wireUI();
    autoplayWhenVisibleOnce();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
